import "@/App.css";
import { Routes, Route } from "react-router-dom";

import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

import ScrollToTop from "@/components/ScrollToTop";

import Header from "@/components/Header";
import Hero from "@/components/Hero";
import TrustStrip from "@/components/TrustStrip";
import CateringPackages from "@/components/CateringPackages";
import CommunityImpact from "@/components/CommunityImpact";
import CTASection from "@/components/CTASection";
import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import BackToTop from "@/components/BackToTop";

import ExperiencePage from "@/components/ExperiencePage";
import PrivateMenuPage from "@/components/PrivateMenuPage";
import { RequireAccount } from "@/components/RequireAccount";
import HolidayDealsPage from "@/components/HolidayDealsPage";
import { HolidayPromo } from "@/components/holiday/HolidayPromo";
import { HolidayFall } from "@/components/holiday/HolidayFall";
import AgencyPortalPage from "@/components/AgencyPortalPage";
import WhoWeServePage from "@/components/WhoWeServePage";
import WhyUsPage from "@/components/WhyUsPage";
import GalleryPage from "@/components/GalleryPage";
import HowWeWorkPage from "@/components/HowWeWorkPage";
import QuotePage from "@/components/QuotePage";
import AuthPage from "@/components/AuthPage";
import CartPage from "@/components/CartPage";
import CheckoutSuccessPage from "@/components/CheckoutSuccessPage";
import PoSuccessPage from "@/components/PoSuccessPage";
import CashSuccessPage from "@/components/CashSuccessPage";
import QuoteReceivedPage from "@/components/QuoteReceivedPage";
import AgencyBatchPage from "@/components/AgencyBatchPage";
import AgencyBatchReceivedPage from "@/components/AgencyBatchReceivedPage";
import CartDrawer from "@/components/CartDrawer";
import EventsPage from "@/components/events/EventsPage";
import KitchenPage from "@/components/kitchen/KitchenPage";
import CalendarsPage from "@/components/CalendarsPage";
import PrintPrepPage from "@/components/kitchen/PrintPrepPage";
import KitchenTicketPage from "@/components/kitchen/KitchenTicketPage";
import RecipeWorksheetPage from "@/components/kitchen/RecipeWorksheetPage";
import EventDetailPage from "@/components/events/EventDetailPage";
import EventSignupPage from "@/components/events/EventSignupPage";
import { Toaster } from "@/components/ui/sonner";
import PartnershipPage from "@/components/PartnershipPage";
import PublicInvoicePage from "@/components/PublicInvoicePage";
import AccountInvoicesPage from "@/components/AccountInvoicesPage";
import AdminOrdersPage from "@/components/AdminOrdersPage";
import SalesReportPage from "@/components/SalesReportPage";
import ForgotPasswordPage from "@/components/ForgotPasswordPage";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import RushPendingPage from "@/components/RushPendingPage";
import QuoteBuilderMockupPage from "@/components/QuoteBuilderMockupPage";

const HomePage = () => (
  <div className="App font-sans bg-white text-brand-charcoal antialiased">
    <Header />
    <main>
      <Hero />
      <TrustStrip />
      <CateringPackages />
      <CommunityImpact />
      <CTASection />
      <FAQ />
    </main>
    <Footer />
    <StickyMobileCTA />
    <BackToTop />
    <HolidayFall />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />

          {/* Decluttered section pages */}
          <Route path="/who-we-serve" element={<WhoWeServePage />} />
          <Route path="/why-us" element={<WhyUsPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/how-we-work" element={<HowWeWorkPage />} />
          <Route path="/quote" element={<QuotePage />} />
          {/* Non-functional visual mockup — remove after real AI quote builder ships. */}
          <Route path="/quote-builder-mockup" element={<QuoteBuilderMockupPage />} />

          {/* Portal entries */}
          <Route path="/menu" element={<RequireAccount><PrivateMenuPage /></RequireAccount>} />
          <Route path="/holiday" element={<HolidayDealsPage />} />
          <Route path="/agency" element={<AgencyPortalPage />} />

          {/* Auth */}
          <Route path="/login" element={<AuthPage defaultMode="login" />} />
          <Route path="/register" element={<AuthPage defaultMode="register" />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/agency/login"
            element={<AuthPage defaultMode="login" defaultRole="agency" />}
          />
          <Route
            path="/agency/register"
            element={<AuthPage defaultMode="register" defaultRole="agency" />}
          />

          {/* Cart / Checkout */}
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/po-success" element={<PoSuccessPage />} />
          <Route path="/checkout/cash-success" element={<CashSuccessPage />} />
          <Route path="/checkout/rush-pending" element={<RushPendingPage />} />
          <Route path="/checkout/quote-received" element={<QuoteReceivedPage />} />
          <Route path="/agency/batch" element={<AgencyBatchPage />} />
          <Route path="/agency/batch/received" element={<AgencyBatchReceivedPage />} />

          {/* Experience deep pages */}
          <Route path="/experience/:slug" element={<ExperiencePage />} />

          {/* Partnership inquiry (separate funnel from catering quote) */}
          <Route path="/partner" element={<PartnershipPage />} />

          {/* Admin-authored invoices with public payment links */}
          <Route path="/invoice/:token" element={<PublicInvoicePage />} />

          {/* Agency self-serve invoices */}
          <Route path="/account/invoices" element={<AccountInvoicesPage />} />

          {/* Admin dashboard (role-guarded inside the component) */}
          <Route path="/admin" element={<AdminOrdersPage />} />
          <Route path="/admin/orders" element={<AdminOrdersPage />} />
          <Route path="/admin/reports/sales" element={<SalesReportPage />} />
          <Route path="/admin/events" element={<EventsPage />} />
          <Route path="/admin/kitchen" element={<KitchenPage />} />
          <Route path="/admin/calendars" element={<CalendarsPage />} />
          <Route path="/admin/kitchen/print" element={<PrintPrepPage />} />
          <Route path="/admin/kitchen/ticket/:orderId" element={<KitchenTicketPage />} />
          <Route path="/admin/kitchen/worksheet" element={<RecipeWorksheetPage />} />
          <Route path="/admin/events/:id" element={<EventDetailPage />} />
          <Route path="/e/:slug" element={<EventSignupPage />} />

          <Route path="*" element={<HomePage />} />
        </Routes>
        <HolidayPromo />

        {/* Global cart drawer (mounted once) */}
        <CartDrawer />
        <Toaster position="top-center" richColors />
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
